import { useState, useEffect, useCallback } from "react";
import useApi from "../useApi";
import axiosInstance from "../axiosInstance";

const BASE = "/system-settings/api-settings";

export const getApiSettings = (params = {}) =>
  axiosInstance.get(BASE, { params }).then((res) => res.data);

export const getApiSettingById = (id) =>
  axiosInstance.get(`${BASE}/${id}`).then((res) => res.data);

export const createApiSetting = (data) =>
  axiosInstance.post(BASE, data).then((res) => res.data);

export const updateApiSetting = (id, data) =>
  axiosInstance.put(`${BASE}/${id}`, data).then((res) => res.data);

export const deleteApiSetting = (id) =>
  axiosInstance.delete(`${BASE}/${id}`).then((res) => res.data);

export const toggleApiSettingStatus = (id) =>
  axiosInstance.patch(`${BASE}/${id}/toggle-active`).then((res) => res.data);

export const testApiConnection = (id) =>
  axiosInstance.post(`${BASE}/${id}/test`).then((res) => res.data);

export const useGetApiSettings = (params = {}) => {
  const {
    page = 1,
    limit = 10,
    search = "",
    type = "",
    active = "",
    sortBy = "name",
    sortOrder = "ASC",
  } = params;

  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const queryParams = { page, limit, sortBy, sortOrder };
      if (search) queryParams.search = search;
      if (type) queryParams.type = type;
      if (active !== "") queryParams.active = active;

      const response = await axiosInstance.get(BASE, { params: queryParams });
      const res = response.data;

      if (res.success) {
        setData(res.data || []);
        if (res.pagination) setPagination(res.pagination);
      } else {
        setError(res.message || "Failed to load API settings");
        setData([]);
      }
    } catch (err) {
      if (err.response) {
        setError(err.response?.data?.message || err.response?.data?.error || `Request failed (${err.response.status})`);
      } else if (err.request) {
        setError("Cannot reach server. Check that the backend is running.");
      } else {
        setError(err.message || "Failed to load API settings");
      }
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, type, active, sortBy, sortOrder]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refetch = useCallback(() => {
    fetchData();
  }, [fetchData]);

  return { data, pagination, loading, error, refetch };
};

export const useCreateApiSetting = () => {
  return useApi(BASE, { method: "POST" });
};

export const useUpdateApiSetting = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (id, payload) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.put(`${BASE}/${id}`, payload);
      return response.data;
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to update API setting";
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
};

export const useDeleteApiSetting = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.delete(`${BASE}/${id}`);
      return response.data;
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to delete API setting";
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
};

export const useToggleApiSettingStatus = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.patch(`${BASE}/${id}/toggle-active`);
      return response.data;
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to toggle status";
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
};

export const useTestApiConnection = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.post(`${BASE}/${id}/test`);
      return response.data;
    } catch (err) {
      const msg = err.response?.data?.message || "Connection test failed";
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
};